import constants from '@helio-shared/remote-survey/constants.json';

export const REMOTE_SURVEY = constants;

// `constants.sections` widens to `string[]` when imported from JSON, so the section
// key union is derived from the survey shape instead (see `SurveySections` below).
export type SurveySectionKey = keyof SurveySections;
export type PhotoType = (typeof constants.photoTypes)[number];
export type SurveyStatus = (typeof constants.statuses)[number];
export type ExceptionType = (typeof constants.exceptionTypes)[number];
export type CapturePath = (typeof constants.capturePaths)[number];

export const SURVEY_SECTIONS = constants.sections as readonly SurveySectionKey[];

export function photoTypesForSection(
  section: SurveySectionKey,
  opts: {
    loftAccessible?: boolean | null;
    existingGeneration?: boolean | null;
    existingSolar?: boolean | null;
    existingBattery?: boolean | null;
    existingEvHeatPump?: boolean | null;
  } = {},
): PhotoType[] {
  const by = constants.photoTypesBySection;
  if (section === 'roof') return [...by.roof] as PhotoType[];
  if (section === 'loft') {
    if (opts.loftAccessible === false) return [];
    return [...by.loft] as PhotoType[];
  }
  if (section === 'electrical') {
    const types = [...by.electrical] as PhotoType[];
    const equipment = [
      [opts.existingSolar, 'existing_inverter'],
      [opts.existingBattery, 'existing_battery'],
      [opts.existingEvHeatPump, 'existing_ev_charger'],
    ] as const;
    const extras = equipment.filter(([on]) => on).map(([, photo]) => photo as PhotoType);
    if (extras.length > 0) {
      types.push(...extras);
    } else if (opts.existingGeneration) {
      types.push(...(by.electricalExisting as PhotoType[]));
    }
    return types;
  }
  if (section === 'battery') return [...by.battery] as PhotoType[];
  if (section === 'inverter') return [...by.inverter] as PhotoType[];
  return [];
}

export function emptySurveySections() {
  return {
    property: {
      address: null as string | null,
      postcode: null as string | null,
      property_type: null as string | null,
      storeys: null as string | null,
      age_band: null as string | null,
      extensions: null as boolean | null,
      loft_conversion: null as boolean | null,
    },
    access: {
      front: false,
      rear: false,
      side: false,
      loft: false,
      garage: false,
      meter: false,
      consumer_unit: false,
      battery_location: false,
      restrictions: '',
    },
    roof: {} as Record<string, unknown>,
    loft: {
      accessible: null as boolean | null,
      not_accessible_reason: null as string | null,
      not_accessible_note: '',
    },
    electrical: {
      existing_solar: null as boolean | null,
      existing_battery: null as boolean | null,
      existing_ev_heat_pump: null as boolean | null,
      existing_generation: null as boolean | null,
      existing_generation_note: '',
    },
    battery: {
      location: null as string | null,
      dry: null as boolean | null,
      ventilated: null as boolean | null,
      accessible: null as boolean | null,
      not_living_accommodation: null as boolean | null,
      combustibles_nearby: null as boolean | null,
      heat_source_nearby: null as boolean | null,
    },
    inverter: {
      preferred_location: '',
      indoor_outdoor: null as string | null,
    },
    system: {
      confirmed: null as boolean | null,
    },
    declaration: {
      statement_accurate: false,
      statement_access: false,
      statement_changes: false,
      accepted: false,
    },
  };
}

export type SurveySections = ReturnType<typeof emptySurveySections>;
